import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Toggle } from "@/components/ui/toggle";
import { MixboardAPI } from "@/lib/mixboard/api";
import { useMixboard } from "@/state/useMixboard";
import { ReferencePanel } from "./ReferencePanel";
import { VersionList } from "./VersionList";
import { TemplatePicker } from "./TemplatePicker";
import { MultiPreview } from "./MultiPreview";

const downloadFile = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function GenerateButton({ draft = false }: { draft?: boolean }) {
  const session = useMixboard((s) => s.session);
  const setSession = useMixboard((s) => s.setSession);
  const loading = useMixboard((s) => s.loading);
  const setLoading = useMixboard((s) => s.setLoading);

  const generate = async () => {
    if (!session) return;
    setLoading(true);
    await MixboardAPI.generate(session.id, draft);
    setSession((prev: any) => ({ ...prev, last_render_url: "/static/mixboard/loading.png" }));
  };

  return (
    <button
      onClick={generate}
      className="bg-green-500 px-4 py-2 rounded text-black font-semibold w-full disabled:opacity-60"
      disabled={!session || loading}
    >
      {loading ? "Generating…" : "Generate Poster"}
    </button>
  );
}

export function Sidebar() {
  const density = 0.5;
  const symmetry = 0.4;
  const detail = 0.6;
  const session = useMixboard((s) => s.session);
  const [versionRefresh, setVersionRefresh] = useState(0);
  const [draft, setDraft] = useState(false);
  const [instruction, setInstruction] = useState("");
  const setSession = useMixboard((s) => s.setSession);
  const setLayers = useMixboard((s) => s.setLayers);
  const setLoading = useMixboard((s) => s.setLoading);
  const setTypographyAssets = useMixboard((s) => s.setTypographyAssets);
  const [socialOutputs, setSocialOutputs] = useState<Record<string, string>>({});

  const saveVersion = async () => {
    if (!session) return;
    await MixboardAPI.saveVersion(session.id, "Draft A");
    setVersionRefresh((r) => r + 1);
  };

  return (
    <div className="w-72 bg-background/40 text-foreground p-4 border-r border-border space-y-6">
      <h2 className="text-xl font-bold">Style Controls</h2>

      <div>
        <label>Density</label>
        <Slider defaultValue={[density]} max={1} step={0.01} />
      </div>
      <div>
        <label>Symmetry</label>
        <Slider defaultValue={[symmetry]} max={1} step={0.01} />
      </div>
      <div>
        <label>Detail</label>
        <Slider defaultValue={[detail]} max={1} step={0.01} />
      </div>

      <Toggle pressed={draft} onPressedChange={setDraft} className="w-full flex items-center justify-between">
        <span>Draft Mode</span>
      </Toggle>
      <GenerateButton draft={draft} />
      <div className="flex gap-2">
        <button
          onClick={async () => {
            if (!session) return;
            const snap = await MixboardAPI.undo(session.id);
            if (snap?.layers) {
              setLayers(snap.layers);
            }
          }}
          className="flex-1 bg-muted rounded py-2"
        >
          Undo
        </button>
        <button
          onClick={async () => {
            if (!session) return;
            const snap = await MixboardAPI.redo(session.id);
            if (snap?.layers) {
              setLayers(snap.layers);
            }
          }}
          className="flex-1 bg-muted rounded py-2"
        >
          Redo
        </button>
      </div>
      <button
        onClick={saveVersion}
        className="bg-indigo-500 w-full py-2 rounded text-foreground font-semibold"
        disabled={!session}
      >
        Save Version
      </button>
      <button
        onClick={async () => {
          if (!session) return;
          const out = await MixboardAPI.compose(session.id);
          const composed = out.composed_url ?? out.image_url;
          if (composed) {
            setSession((prev: any) => ({ ...prev, last_render_url: composed }));
          }
        }}
        className="w-full bg-purple-500 rounded py-2 text-black font-semibold"
      >
        Compose Layers
      </button>
      <div className="space-y-2">
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="e.g. 'more contrast, more symmetry'"
          className="w-full rounded bg-muted text-foreground p-2 text-sm"
        />
        <button
          onClick={async () => {
            if (!session || !instruction) return;
            setLoading(true);
            await MixboardAPI.refine(session.id, instruction);
            setInstruction("");
          }}
          className="w-full bg-amber-500 text-black font-semibold py-2 rounded"
        >
          Refine Image
        </button>
      </div>
      <VersionList sessionId={session?.id} refreshToken={versionRefresh} />
      <div>
        <h3 className="text-sm font-semibold text-foreground/70 mb-2">Templates</h3>
        <TemplatePicker />
      </div>
      <div className="space-y-2">
        <button
          className="w-full bg-pink-500 text-foreground font-semibold rounded py-2"
          onClick={async () => {
            if (!session) return;
            const res = await MixboardAPI.buildTypography(session.id);
            const assets = res.assets ?? [];
            setTypographyAssets(assets);
            setSession((prev: any) => ({ ...prev, typography_assets: assets }));
          }}
        >
          Build Typography Set
        </button>
        <div className="flex gap-2">
          <button
            className="flex-1 bg-cyan-500 text-black font-semibold rounded py-2"
            onClick={async () => {
              if (!session) return;
              const out = await MixboardAPI.multiGenerate(session.id);
              setSocialOutputs(out.outputs ?? {});
            }}
          >
            Social Pack
          </button>
          <button
            className="flex-1 bg-emerald-500 text-black font-semibold rounded py-2"
            onClick={async () => {
              if (!session?.last_render_url) return;
              const out = await MixboardAPI.exportPrint(session.last_render_url);
              if (out?.url) {
                downloadFile(out.url, "poster_print_ready.png");
              }
            }}
          >
            Print Export
          </button>
        </div>
        {Object.keys(socialOutputs).length > 0 && (
          <div className="bg-muted rounded p-2">
            <MultiPreview outputs={socialOutputs} />
          </div>
        )}
      </div>
      <ReferencePanel />
    </div>
  );
}
